import { FC, useContext } from 'react';
import {
  Button,
  Spacing,
  Typography,
  Checkbox,
  StyledDiv,
  IconText,
  TitleText,
} from '@taleemabad/dsm/components';
import styled from '@emotion/styled';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import { ExamContext } from '@taleemabad/providers';
import { IBookChapter, ISchoolClassInfo } from '@taleemabad/db';
import { isUrduText } from '@taleemabad/dsm/utils';

interface ExamGeneratorProps {
  chapters: IBookChapter[];
  errorMessage?: string;
  handleLoadQuestionsClick?: () => void;
  classDetails: ISchoolClassInfo | null;
}

export const ExamGeneratorInputForm: FC<ExamGeneratorProps> = ({
  chapters,
  errorMessage,
  handleLoadQuestionsClick,
  classDetails,
}) => {
  const { selectedOptions, setSelectedOptions } = useContext(ExamContext);

  const handleChaptersSelectionChange = (chapter: IBookChapter) => {
    setSelectedOptions((prevOptions) => {
      const currentSelectedChapters = prevOptions.selectedChapters || [];
      const chapterExists = currentSelectedChapters.some(
        (selectedChapter) => selectedChapter.id === chapter.id,
      );

      const updatedChapters = chapterExists
        ? currentSelectedChapters.filter(
            (selectedChapter) => selectedChapter.id !== chapter.id,
          )
        : [...currentSelectedChapters, chapter];
      return {
        ...prevOptions,
        selectedChapters: updatedChapters,
      };
    });
  };

  return (
    <Container>
      <StickyHeader>
        <Spacing marginBottom="lg">
          <Typography
            variant="textTitleMediumSurfaceVariant"
            data-cy="exam-generator-chapter-list-title"
          >
            Chapter List
          </Typography>
        </Spacing>
        <Spacing marginTop="lg">
          <TitleText
            leftText={`${classDetails?.gradeLabel} - ${classDetails?.section}`}
            seperatorIconName="circle"
            rightText={classDetails?.subjectLabel || ''}
          />
        </Spacing>
      </StickyHeader>
      <Spacing margin="lg none">
        <StyledDiv
          flexDirection="column"
          gap={2}
          background="--md-sys-color-surface-container-high"
          borderRadius="--rk-spacing-md"
          padding="var(--rk-spacing-lg)"
        >
          <Spacing margin="lg">
            <IconText
              data-cy="exam-generator-select-chapters-title"
              icon="import_contacts"
              text="Select Chapters"
              textVariant="textTitleMediumPrimary"
              iconColor="--md-ref-primary-40"
            />
          </Spacing>
          {chapters.map((chapter) => (
            <StyledDiv
              key={chapter.id}
              alignItem="center"
              flexDirection={`${isUrduText(chapter.title) ? 'row-reverse' : 'row'}`}
            >
              <Checkbox
                data-cy="exam-generator-chapter-checkbox"
                onChange={() => handleChaptersSelectionChange(chapter)}
                label={`${chapter.chapterNumber} - ${chapter.title}`}
                direction={`${isUrduText(chapter.title) ? 'rtl' : 'ltr'}`}
                defaultChecked={selectedOptions.selectedChapters?.some(
                  (selectedChapter) => selectedChapter.id === chapter.id,
                )}
              />
            </StyledDiv>
          ))}
        </StyledDiv>
      </Spacing>
      <Typography variant="textBodySmallSchemesError">
        {errorMessage}
      </Typography>
      <SubmitBtn>
        <Button
          data-cy="exam-generator-load-questions-button"
          onClick={handleLoadQuestionsClick}
          disabled={selectedOptions.selectedChapters?.length === 0}
        >
          Load Questions
        </Button>
      </SubmitBtn>
    </Container>
  );
};

const Container = styled.div``;

const StickyHeader = styled.div`
  padding: var(--rk-spacing-lg) var(--rk-spacing-sm);
  position: sticky;
  top: 0;
  z-index: 1;
  background: var(--md-sys-color-on-primary);
`;

const SubmitBtn = styled.div`
  width: -webkit-fill-available;
  padding: var(--rk-spacing-xxl) 0;
  background: var(--md-sys-color-on-primary);
  margin-right: var(--rk-spacing-xl);
  position: fixed;
  bottom: 0;
  ${mq(BreakpointSizes.xs)} {
    md-filled-button {
      width: 100%;
    }
  }
`;
